import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { MemberTagsVisibleInMemberList as getMemberTagsVisibleInMemberList } from "@circle-react/helpers/memberTagsHelper";
import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { MemberTag } from "@circle-react/types";
import { MemberTags } from "@circle-react-uikit/MemberTags";
import { MemberCardImage } from "../../CardView/MemberCardImage";
import { useDetermineConnectionStatus } from "../../hooks/useDetermineConnectionStatus";
import { CardHeader } from "./CardHeader";
import { ExpandableBio } from "./ExpandableBio";

export interface CommunityMemberProps {
  communityMember: any; // Because we have 3 different queries for the member directory, we need to use any here.
  shouldShowHiddenMemberTooltip?: boolean;
}

export const CommunityMember = ({
  communityMember,
  shouldShowHiddenMemberTooltip = false,
}: CommunityMemberProps) => {
  const memberTags = getMemberTagsVisibleInMemberList({
    memberTags: communityMember.member_tags,
  });
  const profileLinkProps = useProfileLinkProps({
    public_uid: communityMember.public_uid,
  });

  const visibleMemberTags = memberTags.filter(
    (memberTag: MemberTag) =>
      memberTag?.is_public && memberTag?.display_locations?.member_directory,
  );

  const memberConnection =
    communityMember.connection_to_current_community_member;

  const { isOtherUserSentConnection } =
    useDetermineConnectionStatus(memberConnection);

  return (
    <li
      className="border-primary bg-primary group rounded-2xl border-[1px] p-6 first:mt-4 first:md:mt-0"
      data-testid="community_member"
    >
      <a className="flex w-full gap-x-4" {...profileLinkProps}>
        <div style={{ aspectRatio: "1 / 1" }} className="h-16">
          <MemberCardImage
            name={communityMember.name}
            src={communityMember.avatar_url}
            className="!rounded-full"
          />
        </div>
        <section className="flex min-w-0 flex-1 flex-col gap-y-3">
          <div className="flex flex-col gap-y-3">
            <CardHeader
              communityMember={communityMember}
              shouldShowHiddenMemberTooltip={shouldShowHiddenMemberTooltip}
            />
            {isOtherUserSentConnection && memberConnection?.note && (
              <div className="-mb-2">
                <ConnectionNote
                  note={memberConnection?.note}
                  lineClamp={1}
                  collapse
                />
              </div>
            )}
            {communityMember.bio && <ExpandableBio bio={communityMember.bio} />}
          </div>
          <MemberTags
            memberTags={visibleMemberTags}
            limit={2}
            memberId={communityMember.id}
          />
        </section>
      </a>
    </li>
  );
};
